import React, { useState, useEffect } from 'react';
import { AppBar, Toolbar, Typography, Button, IconButton, Popover, Box, Container, useTheme, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { app, appSignUp, COLORS, FONT_FAMILY } from '../constants/constants';

export default function Header() {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const location = useLocation();
    const [scrolled, setScrolled] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [bannerHeight, setBannerHeight] = useState(0);

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 0);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        const calculateBannerHeight = () => {
            const banner = document.querySelector('[data-banner-playbuilder]');
            if (banner) {
                const height = banner.offsetHeight;
                setBannerHeight(height);
            } else {
                setBannerHeight(0);
            }
        };

        // Calcular altura inicial con un pequeño delay para asegurar que el DOM esté listo
        const timeoutId = setTimeout(() => {
            calculateBannerHeight();
        }, 0);

        // Observar cambios en el tamaño del banner
        const banner = document.querySelector('[data-banner-playbuilder]');
        let resizeObserver;

        if (banner) {
            resizeObserver = new ResizeObserver(() => {
                calculateBannerHeight();
            });
            resizeObserver.observe(banner);
        }

        // Recalcular en cambios de ruta o redimensionamiento de ventana
        window.addEventListener('resize', calculateBannerHeight);
        
        return () => {
            clearTimeout(timeoutId);
            if (resizeObserver) {
                resizeObserver.disconnect();
            }
            window.removeEventListener('resize', calculateBannerHeight);
        };
    }, [location.pathname]);

    // Componente para el punto indicador
    const DotIndicator = ({ isSelected, isHovered = false }) => (
        <Box
            sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: isSelected ? COLORS.orangeICB : (isHovered ? 'grey' : 'transparent'),
                marginRight: 1,
                transition: 'background-color 0.3s ease',
            }}
        />
    );

    // Función para determinar si una ruta está activa
    const isRouteActive = (path) => {
        if (path === '/' && location.pathname === '/') return true;
        if (path !== '/' && location.pathname.startsWith(path)) return true;
        return false;
    };

    // Componente para botón de navegación con hover
    const NavButton = ({ isSelected, sx, onClick, children, ...props }) => {
        const [isHovered, setIsHovered] = useState(false);

        return (
            <Button
                sx={sx}
                onClick={onClick}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                {...props}
            >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <DotIndicator isSelected={isSelected} isHovered={isHovered} />
                    {children}
                </Box>
            </Button>
        );
    };


    const navButtonSx = (isSelected = false) => ({
        textTransform: 'none',
        fontFamily: FONT_FAMILY,
        fontSize: { xs: '0.875rem', md: '0.9375rem' },
        fontWeight: 600,
        border: '1px solid transparent',
        color: isSelected ? COLORS.orangeICB : 'grey.900',
        '&:hover': {
            color: isSelected ? COLORS.orangeICB : 'grey.900', // Mantiene el color original en hover
            backgroundColor: 'transparent',
        },
    });

    const mobileButtonSx = (isSelected = false) => ({
        ...navButtonSx(isSelected),
        justifyContent: 'flex-start',
        border: '1px solid transparent',
        '&:hover': { border: '1px solid #EE7720' },
    });

    const signInButtonSx = {
        color: '#048cec',
        px: { xs: 1.5, md: 2 },
        py: { xs: 0.75, md: 1 },
        fontSize: { xs: '0.75rem', md: '0.8rem' },
        fontWeight: 600,
        textTransform: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        gap: { xs: 0.5, md: 1 },
        whiteSpace: 'nowrap',
        minWidth: 'fit-content',
        height: { xs: '36px', md: '40px' },
        backgroundColor: 'transparent',
        transition: 'all 0.3s ease',
        '&:hover': {
            backgroundColor: 'rgba(4, 140, 236, 0.1)',
            color: '#0378c4',
        },
    };

    const signUpButtonSx = {
        background: '#048cec',
        color: 'white',
        px: { xs: 1.5, md: 2 },
        py: { xs: 0.75, md: 1 },
        fontSize: { xs: '0.75rem', md: '0.8rem' },
        fontWeight: 600,
        borderRadius: '8px',
        textTransform: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        gap: { xs: 0.5, md: 1 },
        whiteSpace: 'nowrap',
        minWidth: 'fit-content',
        height: { xs: '36px', md: '40px' },
        boxShadow: '0 4px 15px rgba(4, 140, 236, 0.2)',
        transition: 'all 0.3s ease',
        '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 6px 20px rgba(4, 140, 236, 0.3)',
            background: '#048cec',
        },
    };

    return (
        <AppBar
            position="fixed"
            sx={{
                width: '100%',
                top: bannerHeight > 0 ? `${bannerHeight}px` : '0px',
                backgroundColor: scrolled ? 'rgba(255, 255, 255, 0.9)' : 'transparent',
                backdropFilter: scrolled ? 'blur(10px)' : 'none',
                boxShadow: 'none',
                transition: 'background-color 0.3s ease, backdrop-filter 0.3s ease, top 0.3s ease',
            }}
        >
            <Container
                maxWidth={false}
                disableGutters={isMobile}
                sx={{
                    maxWidth: '1300px',
                    mx: 'auto',
                    px: { xs: 0, sm: 2, md: 4 }
                }}
            >
                <Toolbar sx={{ display: 'flex', alignItems: 'center' }}>
                    {/* Logo and Title */}
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Link to="/" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                            <img src="/images/icbBlue.png" alt="iCoachBasketball" style={{ padding: '10px', width: '30px' }} />
                        </Link>
                        <Typography
                            onClick={() => window.location.href = '/'}
                            sx={{
                                cursor: 'pointer',
                                ml: 1,
                                fontWeight: 700,
                                fontFamily: FONT_FAMILY,
                                background: 'linear-gradient(90deg, #048cec 20%, #fca444 100%)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text',
                            }}
                        >
                            iCoachBasketball
                        </Typography>
                    </Box>

                    {/* Desktop Navigation */}
                    {!isMobile && (
                        <Box sx={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 2 }}>
                            <NavButton
                                isSelected={isRouteActive('/')}
                                sx={navButtonSx(isRouteActive('/'))}
                                onClick={() => window.location.href = '/'}
                            >
                                {t('Header11')}
                            </NavButton>
                            <NavButton
                                isSelected={isRouteActive('/pricing')}
                                sx={navButtonSx(isRouteActive('/pricing'))}
                                onClick={() => window.location.href = '/pricing'}
                            >
                                {t('Header03')}
                            </NavButton>
                            <NavButton
                                isSelected={isRouteActive('/clubs')}
                                sx={navButtonSx(isRouteActive('/clubs'))}
                                onClick={() => window.location.href = '/clubs'}
                            >
                                {t('Header04')}
                            </NavButton>
                        </Box>
                    )}

                    {/* Desktop/Mobile Actions */}
                    <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
                        {isMobile ? (
                            <IconButton onClick={() => setMenuOpen(!menuOpen)} sx={{ color: 'black' }}>
                                {menuOpen ? <CloseIcon /> : <MenuIcon />}
                            </IconButton>
                        ) : (
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button variant="text" sx={signInButtonSx} onClick={() => window.location.href = app}>
                                    {t('Header00')}
                                </Button>
                                <Button
                                    variant="contained"
                                    sx={signUpButtonSx}
                                    onClick={() => window.location.href = appSignUp}
                                >
                                    {t('Header01')}
                                </Button>
                            </Box>
                        )}
                    </Box>
                </Toolbar>
            </Container>

            {/* Mobile Menu */}
            {isMobile && (
                <Popover
                    open={menuOpen}
                    anchorReference="anchorPosition"
                    anchorPosition={{ top: bannerHeight + 64, left: 0 }}
                    onClose={() => setMenuOpen(false)}
                    PaperProps={{
                        sx: {
                            width: '100%',
                            bgcolor: 'white',
                            borderRadius: '0 0 16px 16px',
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            position: 'relative',
                            overflow: 'hidden',
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                height: '3px',
                                background: 'linear-gradient(90deg, #048cec 20%, #fca444 100%)',
                                zIndex: 1,
                            }
                        }
                    }}
                >
                    <Box sx={{
                        display: 'flex',
                        p: 3,
                        flexDirection: 'column',
                        gap: 1.5,
                        position: 'relative',
                        zIndex: 2,
                        '& > *': {
                            borderRadius: '8px',
                            transition: 'all 0.3s ease',
                        }
                    }}>
                        <NavButton
                            isSelected={isRouteActive('/pricing')}
                            sx={{
                                ...mobileButtonSx(isRouteActive('/pricing')),
                                py: 1.5,
                                px: 2,
                                borderRadius: '8px',
                                '&:hover': {
                                    backgroundColor: 'rgba(238, 119, 32, 0.08)',
                                    transform: 'translateX(4px)',
                                }
                            }}
                            onClick={() => { setMenuOpen(false); window.location.href = '/pricing'; }}
                        >
                            {t('Header03')}
                        </NavButton>
                        <NavButton
                            isSelected={isRouteActive('/clubs')}
                            sx={{
                                ...mobileButtonSx(isRouteActive('/clubs')),
                                py: 1.5,
                                px: 2,
                                borderRadius: '8px',
                                '&:hover': {
                                    backgroundColor: 'rgba(238, 119, 32, 0.08)',
                                    transform: 'translateX(4px)',
                                }
                            }}
                            onClick={() => { setMenuOpen(false); window.location.href = '/clubs'; }}
                        >
                            {t('Header04')}
                        </NavButton>

                        <Box sx={{
                            height: '1px',
                            background: 'linear-gradient(90deg, transparent, rgba(238, 119, 32, 0.3), transparent)',
                            my: 1
                        }} />

                        <Button variant="text" sx={signInButtonSx} onClick={() => { setMenuOpen(false); window.location.href = app; }}>
                            {t('Header00')}
                        </Button>
                        <Button
                            variant="contained"
                            sx={signUpButtonSx}
                            onClick={() => { setMenuOpen(false); window.location.href = appSignUp; }}
                        >
                            {t('Header01')}
                        </Button>
                    </Box>
                </Popover>
            )}
        </AppBar>
    );
}